import { STORAGE } from '../constants';
import type { MapMeta, MapData, Node, FlashcardData, FlashcardStats } from '../types';

const generateId = (): string => `map_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const DEFAULT_FLASHCARD_TYPE = 'cloze';
const DEFAULT_FLASHCARD_STATUS = 'new';
const FLASHCARD_TYPES = new Set(['cloze', 'definition', 'compare', 'cause', 'example']);
const FLASHCARD_STATUSES = new Set(['new', 'revealed', 'review', 'known']);

export const getMapIndex = (): MapMeta[] => {
  try {
    const raw = localStorage.getItem(STORAGE.MAPS_INDEX);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

export const setMapIndex = (index: MapMeta[]): void => {
  localStorage.setItem(STORAGE.MAPS_INDEX, JSON.stringify(index));
};

export const loadMap = (id: string): MapData | null => {
  try {
    const raw = localStorage.getItem(STORAGE.MAP_PREFIX + id);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const saveMap = (id: string, data: Partial<MapData>): void => {
  localStorage.setItem(STORAGE.MAP_PREFIX + id, JSON.stringify({
    version: STORAGE.VERSION,
    ...data
  }));

  const index = getMapIndex();
  const mapMeta = index.find(m => m.id === id);
  if (mapMeta) {
    mapMeta.updatedAt = new Date().toISOString();
    setMapIndex(index);
  }
};

export const createMap = (name = STORAGE.DEFAULT_MAP_NAME): MapMeta => {
  const id = generateId();
  const now = new Date().toISOString();
  const meta: MapMeta = { id, name, createdAt: now, updatedAt: now, supabase_id: null };

  const index = getMapIndex();
  index.push(meta);
  setMapIndex(index);

  saveMap(id, { nodes: [], connections: [], messages: [], postIts: [], mode: 'brainstormer' });

  return meta;
};

export const deleteMap = (id: string): void => {
  localStorage.removeItem(STORAGE.MAP_PREFIX + id);
  const index = getMapIndex().filter(m => m.id !== id);
  setMapIndex(index);
};

export const renameMap = (id: string, name: string): void => {
  const index = getMapIndex();
  const mapMeta = index.find(m => m.id === id);
  if (mapMeta) {
    mapMeta.name = name;
    mapMeta.updatedAt = new Date().toISOString();
    setMapIndex(index);
  }
};

export const getSettings = (): Record<string, unknown> => {
  try {
    const raw = localStorage.getItem(STORAGE.SETTINGS);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

export const setSettings = (settings: Record<string, unknown>): void => {
  const current = getSettings();
  localStorage.setItem(STORAGE.SETTINGS, JSON.stringify({ ...current, ...settings }));
};

export const clearSetting = (key: string): void => {
  const current = getSettings();
  const updated = { ...current };
  delete updated[key];
  localStorage.setItem(STORAGE.SETTINGS, JSON.stringify(updated));
};

export const hasSummaryFlashcard = (summary: string): boolean =>
  Boolean(summary && /\{[^}]+\}/.test(summary));

export const stripFlashcardMarkers = (summary = ''): string =>
  summary.replace(/[{}]/g, '').replace(/\s+/g, ' ').trim();

interface FlashcardInput {
  summary?: string;
  title?: string;
  description?: string;
  flashcardData?: Partial<FlashcardData> | null;
  isFlashcard?: boolean;
}

export const createFlashcardData = ({ summary = '', title = '', description = '', flashcardData = null, isFlashcard }: FlashcardInput = {}): FlashcardData | null => {
  const normalizedType = FLASHCARD_TYPES.has(flashcardData?.type ?? '') ? flashcardData!.type! : DEFAULT_FLASHCARD_TYPE;
  const normalizedStatus = FLASHCARD_STATUSES.has(flashcardData?.status ?? '') ? flashcardData!.status! : DEFAULT_FLASHCARD_STATUS;
  const inferredFront = hasSummaryFlashcard(summary)
    ? summary.trim()
    : (flashcardData?.front || '').trim() || summary.trim();
  const inferredBack = (flashcardData?.back || '').trim() || stripFlashcardMarkers(description || title || summary);
  const nextIsFlashcard = typeof isFlashcard === 'boolean'
    ? isFlashcard || Boolean(flashcardData)
    : Boolean(flashcardData || hasSummaryFlashcard(summary));

  if (!nextIsFlashcard) return null;

  return {
    type: normalizedType as FlashcardData['type'],
    front: inferredFront || title.trim(),
    back: inferredBack || title.trim(),
    status: normalizedStatus as FlashcardData['status'],
  };
};

export const normalizeNodeFlashcard = <T extends FlashcardInput>(node: T): T => {
  if (!node) return node;
  const flashcardData = createFlashcardData(node);
  return {
    ...node,
    isFlashcard: Boolean(flashcardData),
    flashcardData,
  };
};

export const getFlashcardStats = (nodes: Node[] = []): FlashcardStats => {
  return nodes.reduce((acc, node) => {
    if (!node?.isFlashcard) return acc;
    acc.total += 1;
    const status = node.flashcardData?.status || DEFAULT_FLASHCARD_STATUS;
    if (status === 'known') acc.known += 1;
    else if (status === 'review') acc.review += 1;
    else if (status === 'revealed') acc.revealed += 1;
    else acc.new += 1;
    return acc;
  }, { total: 0, new: 0, revealed: 0, review: 0, known: 0 } as FlashcardStats);
};

export const exportMapData = (id: string) => {
  const index = getMapIndex();
  const meta = index.find(m => m.id === id);
  const data = loadMap(id);

  if (!meta || !data) return null;

  return {
    version: STORAGE.VERSION,
    name: meta.name,
    exportedAt: new Date().toISOString(),
    nodes: data.nodes || [],
    connections: data.connections || [],
    messages: data.messages || [],
    postIts: data.postIts || []
  };
};

export const importMapData = (data: any): MapMeta => {
  if (!data || !Array.isArray(data.nodes)) {
    throw new Error('Formato file non valido');
  }

  const name = data.name || 'Mappa Importata';
  const meta = createMap(name);

  saveMap(meta.id, {
    nodes: data.nodes.map(normalizeNodeFlashcard),
    connections: data.connections || [],
    messages: data.messages || [],
    postIts: data.postIts || [],
    reports: data.reports || [],
    mode: data.mode || 'brainstormer',
    mapContext: data.mapContext || ''
  });

  return meta;
};
